const DENIED_SCHEMES = new Set([
  'javascript',
  'vbscript',
  'data',
  'blob',
  'file',
  'about',
  'chrome',
  'chrome-extension',
  'moz-extension',
  'safari-extension',
]);

const SCHEME_CHARACTERS = /^[a-z][a-z\d+\-.]*$/i;
const ENCODED_COLON = /%3a/i;
const LAST_IGNORED_LEADING_CHAR_CODE = 0x20;

const stripCharactersBrowsersIgnore = (value: string): string => {
  const withoutLineBreaks = value.replace(/[\t\n\r]/g, '');
  let index = 0;
  while (index < withoutLineBreaks.length && withoutLineBreaks.charCodeAt(index) <= LAST_IGNORED_LEADING_CHAR_CODE) {
    index += 1;
  }
  return withoutLineBreaks.slice(index).trim();
};

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

// Percent-decode the scheme so `javascript%3A` and `java%73cript:` are judged
// as javascript. A literal colon marks the normal boundary, decoding only the
// prefix before it. But when the scheme's own colon is percent-encoded and
// that encoding comes before any literal colon, the whole value is decoded
// instead — otherwise a later literal colon (a port, a timestamp, a nested
// URL in the query) would be mistaken for the boundary, the sliced "scheme"
// would contain `/` characters, and a valid return URL would be rejected.
const withDecodedScheme = (value: string): string | null => {
  const colonAt = value.indexOf(':');
  const encodedColonAt = value.search(ENCODED_COLON);
  if (encodedColonAt !== -1 && (colonAt === -1 || encodedColonAt < colonAt)) {
    return safeDecode(value);
  }
  if (colonAt === -1) return safeDecode(value);

  const scheme = safeDecode(value.slice(0, colonAt));
  if (!scheme || !SCHEME_CHARACTERS.test(scheme)) return null;
  return `${scheme}${value.slice(colonAt)}`;
};

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const schemeOf = (url: URL): string => url.protocol.slice(0, -1).toLowerCase();

/**
 * Returns the merchant return URL when it is safe to navigate to, otherwise
 * `null`. Only script, document and browser-internal schemes are refused:
 * publishers send `https:`, `http:` and arbitrary app deep links (with or
 * without a host), so anything else is allowed through.
 */
export const sanitizeMerchantRedirectUrl = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;

  const stripped = stripCharactersBrowsersIgnore(raw);
  if (!stripped) return null;

  const decoded = withDecodedScheme(stripped);
  if (!decoded) return null;

  const decodedUrl = parseUrl(decoded);
  if (!decodedUrl || DENIED_SCHEMES.has(schemeOf(decodedUrl))) return null;

  const strippedUrl = parseUrl(stripped);
  if (strippedUrl && schemeOf(strippedUrl) === schemeOf(decodedUrl)) return stripped;
  return decoded;
};
